const STORAGE_KEY = 'speakerVoiceMediaDurationCache'
const DEFAULT_TTL_MS = 1000 * 60 * 60 * 24 // 24 hours

interface CacheRecord {
  duration: number
  expiresAt: number
}

type PersistedCache = Record<string, CacheRecord>

const memoryCache = new Map<string, CacheRecord>()
const inflightRequests = new Map<string, Promise<number | undefined>>()
let persistedCache: PersistedCache | null = null

const isBrowser = typeof window !== 'undefined'

function loadPersistedCache(): PersistedCache {
  if (!isBrowser) { return {} }

  try {
    const serialized = window.localStorage.getItem(STORAGE_KEY)

    if (!serialized) { return {} }

    const parsed = JSON.parse(serialized) as PersistedCache

    if (!parsed || typeof parsed !== 'object') { return {} }

    return parsed
  } catch (_error) {
    return {}
  }
}

function getPersistedCache(): PersistedCache {
  if (persistedCache) { return persistedCache }

  persistedCache = loadPersistedCache()

  pruneExpiredEntries(persistedCache)
  return persistedCache
}

function persistCache(cache: PersistedCache) {
  if (!isBrowser) { return }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache))
  } catch (_error) {
    // Ignore storage failures (e.g. quota exceeded, private mode)
  }
}

function pruneExpiredEntries(cache: PersistedCache) {
  const now = Date.now()

  Object.keys(cache).forEach(key => {
    if (cache[key].expiresAt <= now) {
      delete cache[key]
    }
  })
}

function getCachedDuration(url: string): number | undefined {
  const now = Date.now()
  const fromMemory = memoryCache.get(url)

  if (fromMemory) {
    if (fromMemory.expiresAt > now) { return fromMemory.duration }

    memoryCache.delete(url)
  }

  const cache = getPersistedCache()
  const record = cache[url]

  if (!record) { return undefined }

  if (record.expiresAt <= now) {
    delete cache[url]
    persistCache(cache)
    return undefined
  }

  memoryCache.set(url, record)
  return record.duration
}

function setCachedDuration(url: string, duration: number, ttlMs: number) {
  const expiresAt = Date.now() + ttlMs
  const record: CacheRecord = { duration, expiresAt }

  memoryCache.set(url, record)

  const cache = getPersistedCache()
  cache[url] = record
  persistCache(cache)
}

function loadDurationFromUrl(url: string): Promise<number | undefined> {
  return new Promise(resolve => {
    if (!isBrowser) {
      resolve(undefined)
      return
    }

    const audio = document.createElement('audio')
    audio.preload = 'metadata'

    const cleanup = () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('error', handleError)
      audio.removeAttribute('src')
      audio.load()
    }

    const handleLoadedMetadata = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : undefined
      cleanup()
      resolve(duration)
    }

    const handleError = () => {
      cleanup()
      resolve(undefined)
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('error', handleError)
    audio.src = url
  })
}

export function rememberRemoteMediaDuration(url: string, duration: number, ttlMs: number = DEFAULT_TTL_MS) {
  if (!Number.isFinite(duration) || duration <= 0) { return }

  setCachedDuration(url, duration, ttlMs)
}

export interface IGetRemoteMediaDurationOptions {
  readonly ttlMs?: number
}

export async function getRemoteMediaDuration(url: string, options?: IGetRemoteMediaDurationOptions): Promise<number | undefined> {
  const cachedDuration = getCachedDuration(url)

  if (cachedDuration !== undefined) { return cachedDuration }

  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS

  if (inflightRequests.has(url)) {
    return inflightRequests.get(url) as Promise<number | undefined>
  }

  const request = loadDurationFromUrl(url)
    .then(duration => {
      if (duration !== undefined) {
        setCachedDuration(url, duration, ttlMs)
      }

      return duration
    })
    .finally(() => {
      inflightRequests.delete(url)
    })

  inflightRequests.set(url, request)
  return request
}

export function clearRemoteMediaDurationCache() {
  memoryCache.clear()
  persistedCache = {}

  if (!isBrowser) { return }

  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (_error) {
    // Ignore storage failures
  }
}
